const en = {
  common: {
    altLogo: "SamperLabs",
  },
  nav: {
    platform: "Platform",
    carnitrack: "CarniTrack",
    agritrack: "AgriTrack",
    vision: "Vision",
    contact: "Contact",
    demo: "Request Demo",
    ariaCloseMenu: "Close menu",
    ariaOpenMenu: "Open menu",
    ariaSiteNav: "Site navigation",
    langEn: "English",
    langTr: "Turkish",
  },
  hero: {
    headlineTr: "",
    softwareFor: "Software for",
    processingLine: "Processing",
    infrastructureLine: "Infrastructure",
    processingInfrastructure: "Processing Infrastructure",
    invisibleSpacer: "Livestock",
    subtitle:
      "SamperLabs powers slaughterhouse operations with real-time tracking, yield data, and operational intelligence — building the foundation of a connected protein supply chain.",
    requestDemo: "Request Demo",
    words: ["Meat", "Protein", "Livestock"],
  },
  dashboard: {
    urlHost: "carnitrack.samperlabs.com",
    sidebarOverview: "Overview",
    sidebarIntake: "Intake",
    sidebarProcessing: "Processing",
    sidebarYield: "Yield",
    sidebarReports: "Reports",
    statIntake: "Intake",
    statProcessing: "Processing",
    statYield: "Yield",
    statRate: "Rate",
    statChangeActive: "Active",
    statChangeOnTarget: "On Target",
    yieldTrend: "Yield Trend",
    processingQueue: "Processing Queue",
    queueWeighing: "Weighing",
    queueProcessing: "Processing",
    queuePending: "Pending",
  },
  problem: {
    label: "The Problem",
    title: "An Industry Running on Fragmentation",
    body:
      "The meat processing industry operates on outdated systems that create inefficiency, reduce traceability, and leave value on the table.",
    items: {
      paper: {
        title: "Paper-Based Operations",
        description:
          "Slaughterhouses rely on spreadsheets, paper logs, and disconnected systems for critical operations.",
      },
      tracking: {
        title: "No Standardized Tracking",
        description:
          "No standardized way to track animals through the processing pipeline from intake to output.",
      },
      visibility: {
        title: "Limited Visibility",
        description:
          "Minimal insight into yield performance, operational efficiency, and processing bottlenecks.",
      },
      supply: {
        title: "Disconnected Supply Chain",
        description: "No connection between livestock producers and processors creates data silos.",
      },
    },
  },
  platform: {
    label: "Platform",
    title: "One System for Processing Operations",
    body:
      "SamperLabs provides a unified platform to manage, track, and analyze slaughterhouse workflows — replacing fragmented tools with integrated intelligence.",
    features: {
      tracking: {
        title: "End-to-End Tracking",
        desc: "Track animals from intake through every processing stage",
      },
      realtime: {
        title: "Real-Time Data",
        desc: "Live operational metrics and instant visibility",
      },
      logs: {
        title: "Structured Logs",
        desc: "Comprehensive processing records and audit trails",
      },
      analytics: {
        title: "Yield Analytics",
        desc: "Deep insights into carcass yield and performance",
      },
    },
    metricProcessed: "Processed",
    metricYieldRate: "Yield Rate",
    metricProcessTime: "Process Time",
    metricDataPoints: "Data Points",
    suffixPerMo: "/mo",
    suffixPercent: "%",
    suffixMin: "min",
    suffixK: "K",
  },
  carnitrack: {
    badge: "Core Product",
    title: "CarniTrack",
    tagline: "Processing Intelligence",
    visit: "Visit CarniTrack",
    features: {
      intake: {
        title: "Intake & Registration",
        desc: "Streamlined animal intake with automatic ID assignment and data capture",
      },
      weight: {
        title: "Weight Tracking",
        desc: "Precise weight capture at every stage with trend analysis",
      },
      stages: {
        title: "Processing Stages",
        desc: "Track progress through each processing step in real-time",
      },
      yield: {
        title: "Yield Analysis",
        desc: "Carcass yield calculations and performance benchmarking",
      },
      compliance: {
        title: "Compliance Logs",
        desc: "Automated audit trails for regulatory compliance",
      },
      history: {
        title: "Historical Data",
        desc: "Complete processing history and trend reporting",
      },
    },
    yieldDistribution: "Yield Distribution",
    gradePrime: "Prime",
    gradeChoice: "Choice",
    gradeSelect: "Select",
    weeklyThroughput: "Weekly Throughput",
    dayMon: "Mon",
    daySun: "Sun",
    performanceScore: "Performance Score",
  },
  agritrack: {
    badge: "Coming Next",
    title: "Extending Upstream with AgriTrack",
    body:
      "SamperLabs expands beyond processing to connect livestock producers, creating a seamless data pipeline from farm to facility.",
    bullets: {
      b1: "Animal-level tracking at farm level",
      b2: "Weight and growth monitoring over time",
      b3: "Seamless data continuity into processing",
      b4: "Producer-processor data sharing",
    },
    visit: "Visit AgriTrack",
    cardTitle: "AgriTrack",
    cardSubtitle: "Integrated Extension",
    flowLabel: "Farm → Processing",
    dataContinuity: "Data Continuity",
    traceability: "Traceability",
    statusConnected: "CONNECTED",
    statusEndToEnd: "END-TO-END",
  },
  why: {
    label: "Why SamperLabs",
    title: "Built for Real Operations",
    reasons: {
      workflows: {
        title: "Built for Slaughterhouse Workflows",
        description:
          "Not generic software adapted to your needs — purpose-built from the ground up for meat processing operations.",
      },
      accuracy: {
        title: "High Data Accuracy",
        description:
          "Precise tracking and traceability that meets regulatory requirements and internal quality standards.",
      },
      scale: {
        title: "Scalable Infrastructure",
        description: "From single-facility operations to multi-site enterprises — the platform grows with you.",
      },
      integration: {
        title: "Built for Integration",
        description: "Ready to connect with scales, RFID systems, and future computer vision capabilities.",
      },
    },
  },
  vision: {
    label: "Our Vision",
    title: "The Future of the Protein Supply Chain",
    body:
      "SamperLabs is building the operating system for meat production — connecting every stage from farm to fork with intelligence and transparency.",
    points: {
      connected: {
        title: "Connected Farms & Processors",
        desc: "Seamless data flow across the supply chain",
      },
      realtime: {
        title: "Real-Time Visibility",
        desc: "Live supply chain tracking and monitoring",
      },
      predictive: {
        title: "Predictive Analytics",
        desc: "AI-powered optimization and forecasting",
      },
      transaction: {
        title: "Transaction Layer",
        desc: "Future marketplace and commerce capabilities",
      },
    },
  },
  cta: {
    title: "Build a Smarter Processing Operation",
    body: "Start digitizing your workflow and unlock real operational insight.",
    requestDemo: "Request Demo",
    contactSales: "Contact Sales",
  },
  footer: {
    about: "About",
    contact: "Contact",
    ariaNav: "Footer links",
    slaughterhouseSoftware: "Slaughterhouse software",
    livestockManagement: "Livestock management",
    proteinSupplyChain: "Protein supply chain",
    copyright: "© {{year}} SamperLabs",
  },
  seo: {
    home: {
      title: "Slaughterhouse Software & Livestock Tracking | SamperLabs",
      description:
        "SamperLabs builds slaughterhouse software, livestock management, and protein supply chain visibility — real-time tracking, yield analytics, and operational intelligence from farm to processing.",
    },
  },
  faq: {
    title: "Frequently asked questions",
    items: [
      {
        q: "What is slaughterhouse management software?",
        a: "Slaughterhouse management software digitizes intake, processing stages, weights, and yield so teams replace paper and spreadsheets with a single system. It improves traceability, speeds reporting, and gives managers live visibility into throughput and performance.",
      },
      {
        q: "How does livestock tracking work with processing?",
        a: "Livestock tracking follows animals from identification at intake through each production step. When connected to producer systems, the same identifiers can flow upstream so processors and farms share consistent records instead of disconnected logs.",
      },
      {
        q: "What is a protein supply chain platform?",
        a: "A protein supply chain platform connects data across farms, logistics, and processing so stakeholders see movement, quality signals, and operational metrics in one place. SamperLabs focuses on the processing core and extends visibility upstream with integrated products.",
      },
      {
        q: "Who is SamperLabs for?",
        a: "SamperLabs is built for meat processing operators, plant managers, and leadership teams that need accurate data, audit-friendly records, and analytics without building custom infrastructure. CarniTrack serves the plant; AgriTrack extends visibility to livestock producers.",
      },
      {
        q: "How is CarniTrack different from generic ERP or MES tools?",
        a: "CarniTrack is purpose-built for slaughterhouse workflows — intake, staging, weights, yield, and compliance-oriented logs — rather than forcing generic manufacturing templates. That means faster adoption and metrics that match how meat processing actually runs.",
      },
    ],
  },
  seoPages: {
    breadcrumb: {
      home: "Home",
    },
    relatedTitle: "Related pages",
    slaughterhouse: {
      title: "Slaughterhouse Software & Meat Processing Tracking | SamperLabs",
      description:
        "Slaughterhouse software for intake-to-yield tracking, operational visibility, and yield analytics. Learn how CarniTrack digitizes meat processing infrastructure.",
      h1: "Slaughterhouse software built for real processing lines",
      intro:
        "Modern slaughterhouses still juggle paper tickets, spreadsheets, and disconnected scales. SamperLabs replaces that fragmentation with slaughterhouse software designed around how animals move through your facility — from intake and identification through staging, weights, and yield — so supervisors see what is happening now, not yesterday.",
      h2_1: "Why does a slaughterhouse need dedicated software?",
      p1:
        "Generic tools rarely model carcass flow, hot-weight capture, or grade and yield in ways that match daily operations. Purpose-built slaughterhouse software aligns screens and data models with your lines, reducing double entry and making it easier to train staff. When every step is logged in one place, audits become a byproduct of normal work instead of a scramble to reconstruct records.",
      h2_2: "How do intake and identification support traceability and throughput?",
      p2:
        "Strong intake and ID practices set the tone for the whole day. Slaughterhouse management software should make registration fast, reduce mis-tags, and connect animals to every weight event downstream. Real-time dashboards help you spot bottlenecks — which stage is backing up, where dwell time is high — so you can adjust crews and equipment before throughput suffers.",
      h2_3: "How do yield analytics improve slaughterhouse operations?",
      p3:
        "Yield is where margin shows up on the ledger. Tracking weights and outcomes at a granular level helps you compare shifts, suppliers, and seasons. CarniTrack focuses on processing intelligence: structured logs, trends, and operational metrics that leadership can trust when negotiating or planning investment.",
      h2_4: "What is CarniTrack and how does it support your plant?",
      p4:
        "CarniTrack is SamperLabs’ core product for the plant floor and back office — the operational layer for meat processing tracking, compliance-oriented records, and analytics. It runs in the browser, integrates with the way your teams already work, and scales from a single site to multi-facility operations.",
      h2_5: "What are the next steps when evaluating slaughterhouse software?",
      p5:
        "If you are evaluating slaughterhouse software, start with your highest-friction steps — usually intake, weight capture, or yield reporting — and map how data should flow. Our team can walk through CarniTrack against your workflow and show how a connected approach reduces manual work and improves visibility across the protein supply chain.",
      cta: "Request a demo",
      ctaHint: "Talk to us about digitizing intake, weights, and yield.",
    },
    livestock: {
      title: "Livestock Management Software & Cattle Tracking | SamperLabs",
      description:
        "Livestock management software and cattle tracking that connect producers with processing data. Explore AgriTrack and the farm-to-facility layer of SamperLabs.",
      h1: "Livestock management software that bridges farm and facility",
      intro:
        "Livestock producers and processors often operate on different systems: spreadsheets at the farm, paper at the gate, and siloed databases in the plant. SamperLabs approaches livestock management as a continuity problem — the same animal should have a coherent record from farm through processing, without manual re-keying at every handoff.",
      h2_1: "What should livestock management software cover?",
      p1:
        "Effective livestock management software combines identification, growth and movement history, and handoff data that processors can trust. For producers, that means less ambiguity about which lots shipped when and how animals performed. For plants, it means cleaner intake and fewer disputes over weights or origin when questions arise.",
      h2_2: "How does cattle tracking stay accurate at scale?",
      p2:
        "Cattle tracking breaks down when tags, paper, and ad hoc files diverge. A single source of truth — with clear ownership of updates and timestamps — keeps audits and customer questions answerable without digging through inboxes. SamperLabs emphasizes structured data so analytics later are possible, not just archival PDFs.",
      h2_3: "What is AgriTrack and how does it help producers?",
      p3:
        "AgriTrack extends SamperLabs upstream from the plant, giving livestock producers tools to align with how processors expect data to arrive. The goal is seamless continuity: farm-level context that matches slaughterhouse software on the other side, so both parties spend less time reconciling and more time improving outcomes.",
      h2_4: "How can farms and processors align on shared metrics?",
      p4:
        "When farms and plants agree on identifiers and key events, both sides can measure what matters — arrival condition, weights, and outcomes — without building custom integrations for every relationship. SamperLabs is building the connected layer so protein supply chain partners can collaborate on data instead of copying it.",
      h2_5: "How do I get started with farm-to-facility traceability?",
      p5:
        "Whether you run feedlots, work with integrated partners, or manage plant intake, we can show how livestock management ties into CarniTrack for end-to-end traceability. Reach out for a conversation tailored to your operation.",
      cta: "Contact sales",
      ctaHint: "Discuss farm-to-facility data and AgriTrack.",
    },
    protein: {
      title: "Protein Supply Chain Platform | Farm-to-Fork Software | SamperLabs",
      description:
        "A protein supply chain platform for visibility from farm to fork — unified processing software, upstream livestock data, and analytics for meat operations.",
      h1: "A protein supply chain platform for the meat industry",
      intro:
        "The protein supply chain spans farms, logistics, processing, and retail, but software often stops at each company’s four walls. SamperLabs is building farm-to-fork software with a practical focus: nail operational truth inside the plant first, then extend visibility outward so partners share compatible data instead of parallel spreadsheets.",
      h2_1: "Why does the protein supply chain need a dedicated platform?",
      p1:
        "Meat and poultry move under strict safety and labeling expectations. A protein supply chain platform should make traceability and operational metrics part of daily workflows, not a monthly export. That requires models that understand animals, batches, and yields — not only SKUs and pallets designed for general manufacturing.",
      h2_2: "How can facilities gain visibility without replacing every system?",
      p2:
        "Most facilities will not replace every system overnight. A sensible platform layers intelligence where teams already capture weights and events, then aggregates for leadership. SamperLabs prioritizes adoption: software that matches how slaughter and livestock teams work, with room to deepen integration over time.",
      h2_3: "How do processing intelligence and upstream data connect partners?",
      p3:
        "CarniTrack anchors the processing side with real-time tracking and yield analytics. AgriTrack brings producer-side context forward. Together they form the backbone of a connected narrative — one chain of records that can support compliance questions, continuous improvement, and eventually richer collaboration between farms and plants.",
      h2_4: "What should protein supply chain analytics measure?",
      p4:
        "Dashboards should reflect throughput, yield, and bottlenecks in language operators understand. SamperLabs invests in operational intelligence so metrics tie to physical reality on the line, helping managers decide where to staff, which suppliers perform, and where waste shows up.",
      h2_5: "How can we explore the SamperLabs vision with your team?",
      p5:
        "If you are responsible for digital transformation across the protein supply chain, we would like to show how SamperLabs unifies processing data and opens the door to deeper partner integration. Request a demo to see the platform direction and current products.",
      cta: "Request demo",
      ctaHint: "See how SamperLabs connects processing and upstream data.",
    },
  },
};

export default en;
